import React, { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Users,
  CalendarDays,
  CheckCircle2,
  Clock,
  XCircle,
  ChevronRight,
  GraduationCap,
} from "lucide-react";
import AttendanceReportFilter from "./AttendanceReportFilter";
import { getAttendanceReport } from "../../../api/elearning/attendance/attendance";
import { getAllCoursesNoPaging } from "../../../api/elearning/courses/courses";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import { exportAttendanceReportToExcel } from "./exportAttendanceReport";
import { exportAttendanceReportToPdf } from "./exportAttendanceReportPdf";

const currentYearMonth = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const StatCard = ({ label, value, icon: Icon, iconWrap, compact = false }) => (
  <div
    className={`bg-white rounded-xl border border-gray-100 shadow-sm flex items-center ${
      compact ? "px-2.5 py-2 gap-2" : "px-4 py-3.5 gap-3"
    }`}
  >
    <div className={`${compact ? "p-1.5" : "p-2.5"} rounded-lg ${iconWrap}`}>
      <Icon size={compact ? 14 : 18} />
    </div>
    <div className="min-w-0">
      <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-wider truncate">
        {label}
      </p>
      <p
        className={`${
          compact ? "text-base" : "text-lg"
        } font-black text-gray-900 leading-tight tabular-nums`}
      >
        {value}
      </p>
    </div>
  </div>
);

const rateTone = (rate) => {
  if (rate >= 90) return "text-emerald-700 bg-emerald-50 border-emerald-200";
  if (rate >= 70) return "text-amber-700 bg-amber-50 border-amber-200";
  return "text-red-700 bg-red-50 border-red-200";
};

const AttendanceReport = () => {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [yearMonth, setYearMonth] = useState(currentYearMonth);
  const [courseId, setCourseId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedKeyword, setDebouncedKeyword] = useState("");
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({
    classes: 0,
    students: 0,
    sessions: 0,
    present: 0,
    late: 0,
    absent: 0,
  });
  const [meta, setMeta] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);
  /** null = danh sách lớp; object = đang xem chi tiết lớp */
  const [selectedClass, setSelectedClass] = useState(null);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedKeyword(searchTerm.trim()), 350);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const courseRes = await getAllCoursesNoPaging({ is_active: true });
        if (cancelled) return;
        const courseList = Array.isArray(courseRes)
          ? courseRes
          : courseRes?.records || courseRes?.data || [];
        setCourses(courseList);
      } catch {
        if (!cancelled) setCourses([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Đổi bộ lọc danh sách lớp → thoát chi tiết
  useEffect(() => {
    setSelectedClass(null);
    setSearchTerm("");
  }, [yearMonth, courseId]);

  const view = selectedClass ? "students" : "classes";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const params = { view };
        if (yearMonth) params.year_month = yearMonth;
        if (courseId) params.course_id = courseId;
        if (selectedClass?.class_id) params.class_id = selectedClass.class_id;
        // Keyword: list lớp tìm theo lớp/khóa; chi tiết HV tìm theo tên HV
        if (debouncedKeyword) params.keyword = debouncedKeyword;

        const res = await getAttendanceReport(params);
        if (cancelled) return;
        setRecords(res.records || []);
        setSummary(
          res.summary || {
            classes: 0,
            students: 0,
            sessions: 0,
            present: 0,
            late: 0,
            absent: 0,
          }
        );
        setMeta(res.meta || {});
      } catch {
        if (!cancelled) {
          showErrorToast("Không thể tải báo cáo điểm danh");
          setRecords([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [view, yearMonth, courseId, selectedClass?.class_id, debouncedKeyword, refreshKey]);

  const filterLabel = useMemo(() => {
    if (!courseId) return "";
    const c = courses.find((x) => String(x.id) === String(courseId));
    return c ? `Khóa: ${c.name}` : "";
  }, [courseId, courses]);

  const handleOpenClass = (row) => {
    setSearchTerm("");
    setDebouncedKeyword("");
    setSelectedClass({
      class_id: row.class_id,
      class_name: row.class_name,
      class_code: row.class_code,
      course_name: row.course_name,
      course_id: row.course_id,
    });
  };

  const handleBack = () => {
    setSearchTerm("");
    setDebouncedKeyword("");
    setSelectedClass(null);
  };

  const fetchAbsenceRecords = async () => {
    if (view !== "students" || !selectedClass?.class_id) return [];
    const params = {
      view: "students",
      detail: "absences",
      class_id: selectedClass.class_id,
    };
    if (yearMonth) params.year_month = yearMonth;
    if (courseId) params.course_id = courseId;
    const detailRes = await getAttendanceReport(params);
    return detailRes.records || [];
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const absenceRecords = await fetchAbsenceRecords();

      const result = await exportAttendanceReportToExcel({
        view,
        records,
        absenceRecords,
        summary,
        meta,
        filterLabel,
        classInfo: selectedClass,
      });
      if (!result.ok) {
        showErrorToast(result.message || "Không thể xuất Excel");
        return;
      }
      showSuccessToast("Đã xuất file Excel");
    } catch {
      showErrorToast("Không thể xuất Excel");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdf = async () => {
    setExportingPdf(true);
    try {
      const absenceRecords = await fetchAbsenceRecords();

      const result = await exportAttendanceReportToPdf({
        view,
        records,
        absenceRecords,
        summary,
        meta,
        filterLabel,
        classInfo: selectedClass,
      });
      if (!result.ok) {
        showErrorToast(result.message || "Không thể xuất PDF");
        return;
      }
      showSuccessToast("Đã xuất file PDF");
    } catch {
      showErrorToast("Không thể xuất PDF");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div className="space-y-4" data-tour="attendance-report">
      <AttendanceReportFilter
        yearMonth={yearMonth}
        onYearMonthChange={setYearMonth}
        courseId={courseId}
        onCourseChange={setCourseId}
        courses={courses}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        isMobile={isMobile}
        onRefresh={() => setRefreshKey((k) => k + 1)}
        onExport={handleExport}
        onExportPdf={handleExportPdf}
        exporting={exporting}
        exportingPdf={exportingPdf}
        canExport={records.length > 0}
        detailClass={selectedClass}
        onBack={handleBack}
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 md:gap-3">
        <StatCard
          compact={isMobile}
          label={view === "classes" ? "Số lớp" : "Học viên"}
          value={
            view === "classes" ? summary.classes ?? 0 : summary.students ?? 0
          }
          icon={view === "classes" ? GraduationCap : Users}
          iconWrap="bg-slate-100 text-slate-600"
        />
        <StatCard
          compact={isMobile}
          label={view === "classes" ? "Học viên" : "Buổi điểm danh"}
          value={
            view === "classes" ? summary.students ?? 0 : summary.sessions ?? 0
          }
          icon={view === "classes" ? Users : CalendarDays}
          iconWrap="bg-sky-50 text-sky-600"
        />
        <StatCard
          compact={isMobile}
          label="Có mặt"
          value={summary.present ?? 0}
          icon={CheckCircle2}
          iconWrap="bg-emerald-50 text-emerald-600"
        />
        <StatCard
          compact={isMobile}
          label="Muộn"
          value={summary.late ?? 0}
          icon={Clock}
          iconWrap="bg-amber-50 text-amber-600"
        />
        <StatCard
          compact={isMobile}
          label="Vắng"
          value={summary.absent ?? 0}
          icon={XCircle}
          iconWrap="bg-red-50 text-red-600"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-16 text-gray-400 text-sm font-semibold">
            <Loader2 className="animate-spin" size={18} />
            Đang tải báo cáo...
          </div>
        ) : records.length === 0 ? (
          <div className="py-16 text-center text-sm text-gray-400 font-semibold">
            {view === "classes"
              ? "Không có lớp nào có dữ liệu điểm danh theo bộ lọc."
              : "Lớp chưa có dữ liệu điểm danh học viên."}
          </div>
        ) : view === "classes" ? (
          isMobile ? (
            <div className="divide-y divide-gray-50">
              {records.map((r) => (
                <button
                  key={r.class_id}
                  type="button"
                  onClick={() => handleOpenClass(r)}
                  className="w-full text-left p-3.5 space-y-2.5 active:bg-red-50/50 transition-colors touch-manipulation"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-gray-900 truncate">
                        {r.class_name}
                      </p>
                      <p className="text-[11px] text-gray-400 font-semibold">
                        {[r.class_code, r.course_name].filter(Boolean).join(" · ") ||
                          "—"}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <span
                        className={`px-2 py-0.5 rounded-md text-[10px] font-bold border ${rateTone(
                          r.attendance_rate
                        )}`}
                      >
                        {r.attendance_rate}%
                      </span>
                      <ChevronRight size={16} className="text-gray-300" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-1.5 text-center">
                    {[
                      ["HV", r.student_count, "text-gray-700"],
                      ["Buổi", r.session_count, "text-sky-700"],
                      ["Có mặt", r.present_count, "text-emerald-700"],
                      ["Vắng", r.absent_count, "text-red-600"],
                    ].map(([label, val, tone]) => (
                      <div key={label} className="rounded-lg bg-gray-50 px-1 py-1.5">
                        <p className="text-[9px] font-bold text-gray-400 uppercase">
                          {label}
                        </p>
                        <p className={`text-sm font-black ${tone}`}>{val}</p>
                      </div>
                    ))}
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80">
                    {[
                      "STT",
                      "Lớp",
                      "Khóa học",
                      "HV",
                      "Buổi",
                      "Có mặt",
                      "Muộn",
                      "Vắng",
                      "Chuyên cần",
                      "",
                    ].map((h) => (
                      <th
                        key={h || "action"}
                        className="px-3 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {records.map((r, idx) => (
                    <tr
                      key={r.class_id}
                      onClick={() => handleOpenClass(r)}
                      className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                    >
                      <td className="px-3 py-2.5 text-xs text-gray-400 font-semibold">
                        {idx + 1}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="text-sm font-bold text-gray-900 group-hover:text-red-600 transition-colors">
                          {r.class_name}
                        </div>
                        {r.class_code ? (
                          <div className="text-[10px] text-gray-400 font-bold">
                            {r.class_code}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-3 py-2.5 text-xs text-gray-600">
                        {r.course_name || "—"}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-bold text-gray-800 text-center">
                        {r.student_count}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-bold text-sky-700 text-center">
                        {r.session_count}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-bold text-emerald-700 text-center">
                        {r.present_count}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-bold text-amber-700 text-center">
                        {r.late_count}
                      </td>
                      <td className="px-3 py-2.5 text-sm font-bold text-red-600 text-center">
                        {r.absent_count}
                      </td>
                      <td className="px-3 py-2.5 text-center">
                        <span
                          className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-bold border ${rateTone(
                            r.attendance_rate
                          )}`}
                        >
                          {r.attendance_rate}%
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-gray-300 group-hover:text-red-400">
                        <ChevronRight size={16} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : isMobile ? (
          <div className="divide-y divide-gray-50">
            {records.map((r) => (
              <div key={r.student_id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-gray-900 truncate">
                      {r.student_name}
                    </p>
                    <p className="text-[11px] text-gray-400 font-semibold">
                      {r.student_code || "—"}
                    </p>
                  </div>
                  <span
                    className={`shrink-0 px-2 py-0.5 rounded-md text-[10px] font-bold border ${rateTone(
                      r.attendance_rate
                    )}`}
                  >
                    {r.attendance_rate}%
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-1.5 text-center">
                  {[
                    ["Có mặt", r.present_count, "text-emerald-700"],
                    ["Muộn", r.late_count, "text-amber-700"],
                    ["Vắng", r.absent_count, "text-red-600"],
                    ["Tổng", r.total_sessions, "text-gray-700"],
                  ].map(([label, val, tone]) => (
                    <div key={label} className="rounded-lg bg-gray-50 px-1 py-1.5">
                      <p className="text-[9px] font-bold text-gray-400 uppercase">
                        {label}
                      </p>
                      <p className={`text-sm font-black ${tone}`}>{val}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80">
                  {[
                    "STT",
                    "Mã HV",
                    "Họ tên",
                    "Có mặt",
                    "Muộn",
                    "Vắng",
                    "Tổng",
                    "Chuyên cần",
                  ].map((h) => (
                    <th
                      key={h}
                      className="px-3 py-2.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider whitespace-nowrap"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {records.map((r, idx) => (
                  <tr
                    key={r.student_id}
                    className="hover:bg-slate-50/60 transition-colors"
                  >
                    <td className="px-3 py-2.5 text-xs text-gray-400 font-semibold">
                      {idx + 1}
                    </td>
                    <td className="px-3 py-2.5 text-xs font-semibold text-gray-600 whitespace-nowrap">
                      {r.student_code || "—"}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-bold text-gray-900 whitespace-nowrap">
                      {r.student_name}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-bold text-emerald-700 text-center">
                      {r.present_count}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-bold text-amber-700 text-center">
                      {r.late_count}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-bold text-red-600 text-center">
                      {r.absent_count}
                    </td>
                    <td className="px-3 py-2.5 text-sm font-bold text-gray-800 text-center">
                      {r.total_sessions}
                    </td>
                    <td className="px-3 py-2.5 text-center">
                      <span
                        className={`inline-flex px-2 py-0.5 rounded-md text-[11px] font-bold border ${rateTone(
                          r.attendance_rate
                        )}`}
                      >
                        {r.attendance_rate}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {records.length > 0 ? (
        <p className="text-[11px] text-gray-400 font-medium px-1">
          {view === "classes"
            ? "Bấm vào lớp để xem chi tiết học viên. "
            : ""}
          Tỷ lệ chuyên cần = (Có mặt + Muộn) / Tổng buổi đã điểm danh × 100.
        </p>
      ) : null}
    </div>
  );
};

export default AttendanceReport;
